import { jwtDecode } from "jwt-decode";
import { createContext, ReactNode, useContext, useState } from "react";

import { api, limparAuthorization, setAuthorization } from "services/axios/api";
import {
  ILogin,
  ITrocaLogin,
  IUsuario,
} from "shared/interfaces/signInInterfaces";
import * as auth from "../../services/auth";

interface AuthContextData {
  signed: boolean;
  user: IUsuario | null;
  signIn(login: ILogin): Promise<IUsuario>;
  signInChange(login: ITrocaLogin): Promise<IUsuario>;
  renovarSessao(): Promise<IUsuario>;
  signOut(): void;
  havePermission(permissions: string[]): boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

/** Mesma regra do havePermission, para roles que ainda não estão no estado (ex.: logo após trocar de usuário). */
export function rolesPossuemPermissao(
  role: string[] | string | undefined | null,
  permissions: string[]
): boolean {
  if (!role) return false;

  const roleArray: string[] = Array.isArray(role) ? role : String(role).split(",");

  if (roleArray.length === 0) return false;

  const userRolesLower = roleArray.map((r) => (r ?? "").toString().toLowerCase().trim());
  return permissions.some((permission) =>
    userRolesLower.includes((permission ?? "").toString().toLowerCase().trim())
  );
}

export function AuthProvider({ children }: AuthProviderProps) {
  const storagedUser = sessionStorage.getItem("@MOF:user");
  const storagedToken = sessionStorage.getItem("@MOF:token");
  let localUser: IUsuario | null = null;

  if (storagedToken && storagedUser) {
    localUser = JSON.parse(storagedUser) as IUsuario;
    api.defaults.headers.Authorization = `Bearer ${storagedToken}`;
  }

  const [user, setUser] = useState<IUsuario | null>(localUser);

  // Resposta do login (novo ou legado — mesmo formato): { usuario, token }.
  function gravarSessao(response: any): IUsuario {
    let localUser = response.usuario;
    const data: any = jwtDecode(response.token);
    localUser.role = data.role.toString().split(",");
    // Expor o id do usuário logado (claim IdUsuario do JWT) para gates de UI.
    if (data?.IdUsuario != null) localUser.idUsuario = Number(data.IdUsuario);

    api.defaults.headers.Authorization = `Bearer ${response.token}`;
    // Troca de usuário: o token anterior pode estar em headers.common das três instâncias (ponte ?hashId=).
    setAuthorization(response.token);

    sessionStorage.setItem("@MOF:user", JSON.stringify(localUser));
    sessionStorage.setItem("@MOF:token", response.token);
    sessionStorage.setItem("@MOF:hashId", localUser.hashSession);

    setUser(localUser);
    return localUser;
  }

  async function signIn(login: ILogin) {
    return gravarSessao(await auth.signIn(login));
  }

  async function signInChange(trocaLogin: ITrocaLogin) {
    return gravarSessao(await auth.signInChange(trocaLogin));
  }

  async function renovarSessao() {
    return gravarSessao(await auth.renovarSessao());
  }

  function havePermission(permissions: string[]): boolean {
    return rolesPossuemPermissao(user?.role, permissions);
  }

  function signOut() {
    setUser(null);
    sessionStorage.clear();
    limparAuthorization();
  }

  return (
    <AuthContext.Provider
      value={{
        signed: user ? true : false,
        user,
        signIn,
        signOut,
        signInChange,
        renovarSessao,
        havePermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("hook must be used with an Provider");
  }

  return context;
}

export { useAuth };
